import useProducts from "../../services/useProducts";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { groupBy, sortBy } from "lodash";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import Category from "./Category";
import CategoryNav from "./CategoryNav";
import { IProductOffering, Option } from "../../types/order";
import Product from "./Product";
import { weekdayNameMap } from "../../utils/datetimeUtils";
import { isFairOpen } from "../../utils/fair";
import { CartContext } from "../../contexts/CartContext";
import { ICartContext } from "../../types/cart";
import useFair from "../../services/useFair";
import dayjs from "dayjs";
import { MessagesContext } from "../../contexts/MessagesContext";
import { IMessagesContext } from "../../types/messages";
import { useParams } from "react-router-dom";
import { formatProductName } from "../../utils/product";

const CATEGORY_ID_PREFIX = "category-section-";
const getToolbarHeight = () => (window.innerWidth >= 600 ? 64 : 56);

const Store = () => {
  const { data: products, isLoading: isLoadingProducts } = useProducts();
  const { data: fair, isLoading: isLoadingFair } = useFair();
  const [item, setItem] = useState<null | string | Option>(null);
  const cartRef = useRef(useContext(CartContext) as ICartContext);
  const messagesRef = useRef(useContext(MessagesContext) as IMessagesContext);
  const [closedSeedDialogOpen, setClosedSeedDialogOpen] = useState(false);
  const { slug } = useParams();
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const isScrollingRef = useRef(false);

  const categories = useMemo(() => {
    if (!products) return [];
    const grouped = products.map((offering: IProductOffering) => ({
      name: offering.product.category.name,
      order: offering.product.category.order,
      offering,
    }));
    const sorted = sortBy(grouped, ["order", "name"]);
    const byCategory = groupBy(sorted, "name");
    return Object.keys(byCategory).map((categoryName) => ({
      name: categoryName,
      products: sortBy(
        byCategory[categoryName].map((i) => i.offering),
        "product.name",
      ),
    }));
  }, [products]);

  useEffect(() => {
    if (!fair || !slug) {
      return;
    }

    const cartInvalidated = cartRef.current.syncCartFair(slug, fair.id);
    if (cartInvalidated) {
      messagesRef.current.addMessage({
        closeable: true,
        content:
          "A feira mudou e seu carrinho foi esvaziado. Adicione os itens novamente.",
        severity: "warning",
      });
    }
  }, [fair, slug]);

  useEffect(() => {
    if (fair) {
      if (!isFairOpen(fair)) {
        cartRef.current.emptyCart();
        setClosedSeedDialogOpen(true);
      }
    }
  }, [cartRef, fair, setClosedSeedDialogOpen]);

  useEffect(() => {
    if (item !== null || categories.length === 0) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking || isScrollingRef.current) return;
      ticking = true;
      requestAnimationFrame(() => {
        const toolbarHeight = getToolbarHeight();
        const navEl = document.getElementById("store-toolbar");
        const storeToolbarHeight = navEl?.offsetHeight ?? 0;
        const offset = toolbarHeight + storeToolbarHeight + 4;

        let activeIdx = 0;
        for (let i = 0; i < categories.length; i++) {
          const el = document.getElementById(`${CATEGORY_ID_PREFIX}${i}`);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= offset) {
              activeIdx = i;
            }
          }
        }
        setActiveCategoryIndex(activeIdx);
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [item, categories]);

  const handleCategoryClick = useCallback((index: number) => {
    const el = document.getElementById(`${CATEGORY_ID_PREFIX}${index}`);
    if (!el) return;

    isScrollingRef.current = true;
    setActiveCategoryIndex(index);

    const toolbarHeight = getToolbarHeight();
    const navEl = document.getElementById("store-toolbar");
    const storeToolbarHeight = navEl?.offsetHeight ?? 0;
    const offset = toolbarHeight + storeToolbarHeight + 4;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 1000);
  }, []);

  if (isLoadingProducts || isLoadingFair) {
    return <CircularProgress />;
  }

  if (!products || !fair) {
    return null;
  }

  const filtered = products.filter((offering: IProductOffering) => {
    if (!item) {
      return true;
    }

    if (typeof item === "string") {
      const tokens = item.split(" ");
      return tokens
        .map((token) => token.toLowerCase())
        .every((token) => {
          const productName = offering.product.name.toLowerCase();
          const producer = offering.product.producer.name.toLowerCase();
          return productName.includes(token) || producer.includes(token);
        });
    }

    return offering.id === item.value;
  });

  const onItemFilter: (_: any, newValue: null | string | Option) => void = (
    _,
    newValue,
  ) => {
    setItem(newValue);
  };

  const items: Option[] = products.map((offering: IProductOffering) => ({
    label: `${formatProductName(offering.product)} - ${offering.product.producer.name}`,
    value: offering.id,
  }));

  const sortedItems = sortBy(items, "label");

  const startMoment = dayjs(fair.open_at);
  const endMoment = dayjs(fair.close_at);

  return (
    <div>
      <Dialog
        open={closedSeedDialogOpen}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        onClose={() => {
          setClosedSeedDialogOpen(false);
        }}
      >
        <DialogTitle id="dialog-title">Feira fechada</DialogTitle>
        <DialogContent>
          <DialogContentText id="dialog-description">
            O horário de funcionamento da feira é de{" "}
            {weekdayNameMap[startMoment.local().day()]} às{" "}
            {startMoment.local().format("HH:mm")} até{" "}
            {weekdayNameMap[endMoment.local().day()]} às{" "}
            {endMoment.local().format("HH:mm")}.
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Box sx={{ px: "2rem", pb: "2rem" }}>
        <Box
          id="store-toolbar"
          sx={{
            position: "sticky",
            top: { xs: 56, sm: 64 },
            zIndex: 1099,
            backgroundColor: "#f5f2e5",
            borderBottom: "1px solid rgba(68, 119, 55, 0.24)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mx: "-2rem",
            px: "2rem",
            py: 1.5,
            mb: 2,
          }}
        >
          <Autocomplete
            value={item}
            onChange={onItemFilter}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            disablePortal
            options={sortedItems}
            sx={{ minWidth: { md: 300 }, flexShrink: 0 }}
            renderInput={(params) => (
              <TextField {...params} label="Pesquisar" size="small" />
            )}
            freeSolo
          />
          {item === null && categories.length > 0 && (
            <CategoryNav
              categories={categories.map((c) => c.name)}
              activeIndex={activeCategoryIndex}
              onCategoryClick={handleCategoryClick}
            />
          )}
        </Box>
        <Grid container spacing={4}>
          {item !== null
            ? filtered.map((offering: IProductOffering) =>
                offering.stock > 0 || offering.unlimited_stock ? (
                  <Product product={offering} key={offering.id} />
                ) : null,
              )
            : categories.map((category, index) => (
                <Category
                  category={category}
                  key={category.name}
                  id={`${CATEGORY_ID_PREFIX}${index}`}
                />
              ))}
        </Grid>
      </Box>
    </div>
  );
};

export default Store;
